import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  DataObject as DataObjectIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
  Code as CodeIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
  PlayArrow as PlayIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import BreadcrumbsNav from '../components/BreadcrumbsNav';

const ProjectExecutionResults = () => {
  const { companyId, projectId, executionId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const [execution, setExecution] = useState(null);
  const [stepResults, setStepResults] = useState([]);
  const [outputFiles, setOutputFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [filePreviewDialog, setFilePreviewDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchExecutionDetails = useCallback(async () => {
    setLoading(!execution); // Only show main loading on first load
    setError('');
    try {
      const response = await api.get(`/companies/${companyId}/projects/${projectId}/executions/${executionId}`);
      setExecution(response.data.execution);
      setStepResults(response.data.stepResults || []);
      setOutputFiles(response.data.outputFiles || []);
      
      // Auto-refresh for in-progress executions
      if (response.data.execution.status === 'in_progress' || response.data.execution.status === 'waiting_input') {
        setAutoRefresh(true);
      } else {
        setAutoRefresh(false);
      }
    } catch (err) {
      setError('Failed to fetch execution details');
      console.error('Execution details fetch error:', err);
      setAutoRefresh(false);
    }
    setLoading(false);
  }, [companyId, projectId, executionId, execution]);

  useEffect(() => {
    if (companyId && projectId && executionId) {
      fetchExecutionDetails();
    }
  }, [companyId, projectId, executionId]);

  // Set default tab based on execution status
  useEffect(() => {
    if (execution) {
      // For incomplete or failed executions, default to Steps tab (index 1)
      // For completed executions, default to Results tab (index 0)
      if (execution.status !== 'completed' && activeTab === 0) {
        setActiveTab(1);
      }
    }
  }, [execution, activeTab]);

  // Auto-refresh for in-progress executions
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchExecutionDetails();
      }, 5000); // Refresh every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchExecutionDetails]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      if (selectedFile?.blob && selectedFile?.previewUrl) {
        URL.revokeObjectURL(selectedFile.previewUrl);
      }
    };
  }, [selectedFile]);

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      // Validate parameters
      if (!fileId) {
        console.error('❌ Download failed: fileId is undefined or null');
        alert('Download failed: File ID is missing');
        return;
      }
      
      if (!fileName) {
        console.error('❌ Download failed: fileName is undefined or null');
        alert('Download failed: File name is missing');
        return;
      }
      
      // Try multiple possible endpoints
      // The correct backend route is /api/flows/executions/:executionId/files/:fileId/preview
      const endpoints = [
        // Correct route structure (highest priority)
        `/flows/executions/${executionId}/files/${fileId}/preview`,
        `/flows/executions/${executionId}/files/${fileId}/download`,
        // Alternative route structures
        `/api/flows/executions/${executionId}/files/${fileId}/preview`,
        `/api/flows/executions/${executionId}/files/${fileId}/download`,
        // Company/project specific routes (if they exist)
        `/api/companies/${companyId}/projects/${projectId}/executions/${executionId}/files/${fileId}/preview`,
        `/api/companies/${companyId}/projects/${projectId}/executions/${executionId}/files/${fileId}/download`
      ];
      
      let response;
      let successfulEndpoint = '';
      
      // Try each endpoint until one works
      for (let i = 0; i < endpoints.length; i++) {
        try {
          response = await api.get(endpoints[i], { responseType: 'blob' });
          successfulEndpoint = endpoints[i];
          break;
        } catch (error) {
          if (i === endpoints.length - 1) {
            // All endpoints failed
            throw new Error(`All download endpoints failed. Last error: ${error.response?.status || 'Unknown'}`);
          }
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('❌ Download failed:', error);
      alert(`Failed to download file: ${error.message || 'Unknown error'}. Please check file permissions.`);
    }
  };

  // Helper function to check if file is an image
  const isImageFile = (fileName) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  };

  // Helper function to check if file is a PDF
  const isPdfFile = (fileName) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return extension === '.pdf';
  };

  // Helper function to check if file can be previewed
  const isPreviewableFile = (fileName) => {
    return isImageFile(fileName) || isPdfFile(fileName);
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle file click to show preview
  const handleFileClick = (file) => {
    // For input files, use filename as the identifier (this is what the backend expects)
    // For output files, use fileId
    const fileIdentifier = file.filename || file.fileId;
    
    if (!fileIdentifier) {
      console.error('No file identifier found:', file);
      alert('Unable to preview file: file identifier missing');
      return;
    }
    
    // For previewable files (images and PDFs), we need to use the download endpoint to get the decrypted file
    // The download endpoint will handle S3 decryption and return the file as a blob
    if (isPreviewableFile(file.originalName || file.fileName)) {
      // First, open the dialog with loading state
      const selectedFileData = {
        ...file,
        previewUrl: null, // Will be set after download
        isImage: isImageFile(file.originalName || file.fileName),
        isPdf: isPdfFile(file.originalName || file.fileName),
        isPreviewable: isPreviewableFile(file.originalName || file.fileName),
        isLoading: true,
        // Ensure we have the right file identifier for download
        filename: file.filename,
        fileId: file.fileId,
        fileName: file.fileName,
        originalName: file.originalName
      };
      
      setSelectedFile(selectedFileData);
      setFilePreviewDialog(true);
      
      // Then fetch the decrypted file - try multiple possible endpoints
      // The correct backend route is /api/flows/executions/:executionId/files/:fileId/preview
      const endpoints = [
        // Correct route structure (highest priority)
        `/flows/executions/${executionId}/files/${fileIdentifier}/preview`,
        `/flows/executions/${executionId}/files/${fileIdentifier}/download`,
        // Alternative route structures
        `/api/flows/executions/${executionId}/files/${fileIdentifier}/preview`,
        `/api/flows/executions/${executionId}/files/${fileIdentifier}/download`,
        // Company/project specific routes (if they exist)
        `/api/companies/${companyId}/projects/${projectId}/executions/${executionId}/files/${fileIdentifier}/preview`,
        `/api/companies/${companyId}/projects/${projectId}/executions/${executionId}/files/${fileIdentifier}/download`
      ];
      
      // Try each endpoint until one works
      let endpointIndex = 0;
      const tryEndpoint = () => {
        if (endpointIndex >= endpoints.length) {
          // All endpoints failed, try direct fetch to bypass API base URL issue
          
          // Try the correct route structure first using proper API configuration
          const correctUrl = `${api.defaults.baseURL}/flows/executions/${executionId}/files/${fileIdentifier}/preview`;
          
          fetch(correctUrl, {
            method: 'GET',
            headers: {
              'Accept': 'image/*,application/octet-stream'
            }
          }).then(response => {
            if (response.ok) {
              return response.blob();
            } else {
              throw new Error(`Correct route direct fetch failed: ${response.status}`);
            }
          }).then(blob => {
            const previewUrl = URL.createObjectURL(blob);
            setSelectedFile(prev => ({
              ...prev,
              previewUrl,
              isLoading: false,
              blob: blob,
              // Ensure we preserve the original file properties
              filename: prev.filename,
              fileId: prev.fileId,
              fileName: prev.fileName,
              originalName: prev.originalName
            }));
          }).catch(correctError => {
            console.error('❌ Correct route direct fetch failed:', correctError);
            
            // Try the company/project structure as fallback using proper API configuration
            const companyProjectUrl = `${api.defaults.baseURL}/companies/${companyId}/projects/${projectId}/executions/${executionId}/files/${fileIdentifier}/preview`;
            
            fetch(companyProjectUrl, {
              method: 'GET',
              headers: {
                'Accept': 'image/*,application/octet-stream'
              }
            }).then(response => {
              if (response.ok) {
                return response.blob();
              } else {
                throw new Error(`Company/project direct fetch failed: ${response.status}`);
              }
            }).then(blob => {
              const previewUrl = URL.createObjectURL(blob);
              setSelectedFile(prev => ({
                ...prev,
                previewUrl,
                isLoading: false,
                blob: blob,
                // Ensure we preserve the original file properties
                filename: prev.filename,
                fileId: prev.fileId,
                fileName: prev.fileName,
                originalName: prev.originalName
              }));
            }).catch(companyError => {
              console.error('❌ Company/project direct fetch also failed:', companyError);
              setSelectedFile(prev => ({
                ...prev,
                isLoading: false,
                error: `All endpoints failed. Correct route: ${correctError.message}, Company/project: ${companyError.message}. Please check file permissions and API configuration.`,
                // Ensure we preserve the original file properties
                filename: prev.filename,
                fileId: prev.fileId,
                fileName: prev.fileName,
                originalName: prev.originalName
              }));
            });
          });
          return;
        }
        
        const currentEndpoint = endpoints[endpointIndex];
        
        api.get(currentEndpoint, {
          responseType: 'blob',
          headers: {
            'Accept': 'image/*,application/octet-stream'
          }
        }).then(response => {
          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/*' });
          const previewUrl = URL.createObjectURL(blob);
          
          setSelectedFile(prev => ({
            ...prev,
            previewUrl,
            isLoading: false,
            blob: blob,
            // Ensure we preserve the original file properties
            filename: prev.filename,
            fileId: prev.fileId,
            fileName: prev.fileName,
            originalName: prev.originalName
          }));
        }).catch(error => {
          console.error(`❌ Endpoint ${endpointIndex + 1} failed:`, currentEndpoint, {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message
          });
          endpointIndex++;
          tryEndpoint(); // Try next endpoint
        });
      };
      
      tryEndpoint();
    } else {
      // For non-previewable files, just show the file info
      const selectedFileData = {
        ...file,
        previewUrl: null,
        isImage: false,
        isPdf: false,
        isPreviewable: false,
        isLoading: false,
        // Ensure we have the right file identifier for download
        filename: file.filename,
        fileId: file.fileId,
        fileName: file.fileName,
        originalName: file.originalName
      };
      
      setSelectedFile(selectedFileData);
      setFilePreviewDialog(true);
    }
  };

  // Helper function to get column width styling
  const getColumnWidth = (width) => {
    if (!width || width === 'auto') return undefined;
    
    // Handle preset widths
    switch (width) {
      case 'narrow': return '100px';
      case 'medium': return '150px';
      case 'wide': return '200px';
      default: return width; // Use custom values like '120px', '20%', etc.
    }
  };

  // Helper function to format cell value based on column type
  const formatCellValue = (value, column) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    switch (column.type) {
      case 'currency':
        // Handle currency formatting
        if (typeof value === 'string' && /^[A-Za-z$€£¥₹₽]+/.test(value)) {
          return value; // Already formatted
        }
        
        let numValue = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ''));
        if (isNaN(numValue)) return String(value);
        
        const currencySymbol = column.currencySymbol || '$';
        const thousandsSeparator = column.thousandsSeparator || ',';
        const decimalSeparator = column.decimalSeparator || '.';
        const decimalPlaces = column.decimalPlaces !== undefined ? column.decimalPlaces : 2;
        
        const formattedNumber = numValue.toFixed(decimalPlaces);
        const [integerPart, decimalPart] = formattedNumber.split('.');
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
        
        if (decimalPlaces > 0 && decimalPart) {
          return `${currencySymbol}${formattedInteger}${decimalSeparator}${decimalPart}`;
        } else {
          return `${currencySymbol}${formattedInteger}`;
        }
        
      case 'number':
        // Handle both numeric values and string values with intelligent parsing
        let num;
        if (typeof value === 'number') {
          num = value;
        } else {
          // Use column-specific separators for intelligent parsing
          const numberThousandsSeparator = column.thousandsSeparator || ',';
          const numberDecimalSeparator = column.decimalSeparator || '.';
          let stringValue = String(value);
          
          // Intelligent parsing based on column configuration
          if (stringValue.includes(numberThousandsSeparator) && stringValue.includes(numberDecimalSeparator) && numberThousandsSeparator !== numberDecimalSeparator) {
            // Both separators present and different - parse accordingly
            const lastDecimalIndex = stringValue.lastIndexOf(numberDecimalSeparator);
            const integerPart = stringValue.substring(0, lastDecimalIndex).replace(new RegExp('\\' + numberThousandsSeparator, 'g'), '');
            const decimalPart = stringValue.substring(lastDecimalIndex + 1);
            num = parseFloat(`${integerPart}.${decimalPart}`);
          } else if (stringValue.includes(numberThousandsSeparator) && numberThousandsSeparator !== '.') {
            // Only thousands separators (and they're not dots)
            num = parseFloat(stringValue.replace(new RegExp('\\' + numberThousandsSeparator, 'g'), ''));
          } else if (stringValue.includes('.') && numberThousandsSeparator === '.' && numberDecimalSeparator !== '.') {
            // Dots are thousands separators, not decimal points
            num = parseFloat(stringValue.replace(/\./g, ''));
          } else if (stringValue.includes(numberDecimalSeparator) && numberDecimalSeparator !== '.') {
            // Custom decimal separator (not dot)
            num = parseFloat(stringValue.replace(numberDecimalSeparator, '.'));
          } else {
            // Default parsing - handle as standard number
            let cleanNumber = stringValue.replace(/[^0-9.-]/g, ''); // Remove all non-numeric except decimal point and minus
            num = parseFloat(cleanNumber);
          }
        }
        
        if (isNaN(num)) return String(value);
        
        const numberDecimalPlaces = column.decimalPlaces !== undefined ? column.decimalPlaces : 0;
        const numberThousandsSeparator = column.thousandsSeparator || ',';
        const numberDecimalSeparator = column.decimalSeparator || '.';
        
        const formattedNum = num.toFixed(numberDecimalPlaces);
        const [numIntegerPart, numDecimalPart] = formattedNum.split('.');
        const formattedNumInteger = numIntegerPart.replace(/\B(?=(\d{3})+(?!\d))/g, numberThousandsSeparator);
        
        if (numberDecimalPlaces > 0 && numDecimalPart) {
          return `${formattedNumInteger}${numberDecimalSeparator}${numDecimalPart}`;
        } else {
          return formattedNumInteger;
        }
        
      case 'date':
        try {
          if (!value) return '-';
          
          let dateValue;
          if (value instanceof Date) {
            dateValue = value;
          } else {
            const dateStr = String(value).trim();
            if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
              dateValue = new Date(dateStr);
            } else if (dateStr.match(/^\d{2}[./-]\d{2}[./-]\d{4}$/)) {
              const parts = dateStr.split(/[./-]/);
              dateValue = new Date(parts[2], parts[1] - 1, parts[0]);
            } else {
              dateValue = new Date(dateStr);
            }
          }
          
          if (isNaN(dateValue.getTime())) return String(value);
          
          const dateFormat = column.dateFormat || 'MM/dd/yyyy';
          const day = dateValue.getDate().toString().padStart(2, '0');
          const month = (dateValue.getMonth() + 1).toString().padStart(2, '0');
          const year = dateValue.getFullYear();
          
          switch (dateFormat) {
            case 'dd.MM.yyyy': return `${day}.${month}.${year}`;
            case 'dd/MM/yyyy': return `${day}/${month}/${year}`;
            case 'MM/dd/yyyy': return `${month}/${day}/${year}`;
            case 'yyyy-MM-dd': return `${year}-${month}-${day}`;
            default: return `${month}/${day}/${year}`;
          }
        } catch (error) {
          return String(value);
        }
        
      case 'boolean':
        return value ? 'Yes' : 'No';
      default:
        return String(value);
    }
  };

  const getStepIcon = (stepType, status) => {
    const iconProps = { fontSize: 'small' };
    
    if (status === 'failed') return <ErrorIcon color="error" {...iconProps} />;
    if (status === 'completed') return <CheckCircleIcon color="success" {...iconProps} />;
    if (status === 'in_progress') return <ScheduleIcon color="warning" {...iconProps} />;
    
    switch (stepType) {
      case 'file_upload':
      case 'file_input':
        return <AssignmentIcon {...iconProps} />;
      case 'ai_extraction':
      case 'data_extraction':
        return <DataObjectIcon {...iconProps} />;
      case 'api_call':
      case 'enhanced_api_call':
        return <CodeIcon {...iconProps} />;
      case 'data_transformation':
      case 'variable_operations':
        return <AssessmentIcon {...iconProps} />;
      case 'end':
        return <CheckCircleIcon {...iconProps} />;
      default:
        return <PlayArrowIcon {...iconProps} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'in_progress': return 'warning';
      case 'waiting_input': return 'info';
      default: return 'default';
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const handleViewStepResult = (result) => {
    setSelectedResult(result);
    setDialogOpen(true);
  };

  const getVariableData = (variableName) => {
    if (!execution?.variables) return null;
    return execution.variables[variableName];
  };

  const resolveTemplateVariables = (text) => {
    if (typeof text !== 'string') {
      return text;
    }
    
    return text.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
      const value = getVariableData(varName);
      return value !== undefined ? String(value) : match;
    });
  };

  // Function to render execution results based on End node configuration
  const renderExecutionResults = () => {
    // Handle different execution states
    if (execution.status === 'failed') {
      return (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Execution Failed
          </Typography>
          <Typography variant="body2">
            The workflow execution encountered an error and could not be completed. Please check the execution steps for error details.
          </Typography>
        </Alert>
      );
    }
    
    if (execution.status === 'in_progress' || execution.status === 'waiting_input') {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Execution In Progress
          </Typography>
          <Typography variant="body2">
            The workflow is currently {execution.status === 'waiting_input' ? 'waiting for user input' : 'in progress'}. 
            You can monitor the execution steps to see what has been completed and what's pending.
          </Typography>
        </Alert>
      );
    }
    
    if (execution.status === 'cancelled') {
      return (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Execution Cancelled
          </Typography>
          <Typography variant="body2">
            The workflow execution was cancelled before completion. You can view the completed steps in the Steps tab.
          </Typography>
        </Alert>
      );
    }

    // Find the End node step result for completed executions
    const endStepResult = stepResults.find(result => 
      result.stepType === 'end' && result.status === 'completed'
    );
    
    if (!endStepResult || !endStepResult.output) {
      // Fallback to generic completion message
      return (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Workflow Execution Completed!
          </Typography>
          <Typography variant="body2">
            All steps have been executed successfully. You can view the detailed results in the Steps tab.
          </Typography>
        </Alert>
      );
    }
    
    const result = endStepResult.output;
    const interfaceType = result.interfaceType || 'summary_card';
    const rawResultsTitle = result.resultsTitle || 'Execution Results';
    const resultsTitle = resolveTemplateVariables(rawResultsTitle);
    const formattedOutput = result.formattedOutput;
    const exportConfig = result.exportConfiguration;
    
    console.log('🎨 End step result found:', { 
      hasResult: !!result, 
      interfaceType, 
      rawResultsTitle,
      resultsTitle, 
      hasFormattedOutput: !!formattedOutput,
      hasExportConfig: !!exportConfig,
      resultKeys: Object.keys(result)
    });
    
    return (
      <Card elevation={3} sx={{ border: '2px solid #4caf50', borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {resultsTitle}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Workflow completed successfully
              </Typography>
            </Box>
          </Box>
          
          {formattedOutput ? renderInterfaceOutput(formattedOutput, interfaceType) : renderDefaultOutput(result)}
          
          {exportConfig && exportConfig.enabled && renderExportOptions(exportConfig)}
        </CardContent>
      </Card>
    );
  };

  // Render interface output based on type
  const renderInterfaceOutput = (formattedOutput, interfaceType) => {
    switch (interfaceType) {
      case 'summary_card':
        return renderSummaryCard(formattedOutput);
      case 'data_table':
        return renderDataTable(formattedOutput);
      case 'detailed_report':
        return renderDetailedReport(formattedOutput);
      case 'dashboard':
        return renderDashboard(formattedOutput);
      case 'custom_template':
        return renderCustomTemplate(formattedOutput);
      default:
        return renderDefaultOutput(formattedOutput);
    }
  };

  // Summary Card Interface
  const renderSummaryCard = (output) => {
    const fields = output.fields || [];
    
    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Summary
        </Typography>
                 <Grid container spacing={2}>
           {fields.map((field, index) => (
             <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Paper sx={{ p: 2, backgroundColor: field.priority === 'high' ? '#e8f5e8' : '#f5f5f5' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {field.label}
                </Typography>
                <Typography variant="body1">
                  {field.formattedValue || field.value || '-'}
                </Typography>
                {field.priority === 'high' && (
                  <Chip label="Important" size="small" color="success" sx={{ mt: 1 }} />
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Data Table Interface
  const renderDataTable = (output) => {
    console.log('🎨 renderDataTable called with:', output);
    
    if (output.error) {
      return (
        <Alert severity="error">
          <Typography variant="subtitle2">Table Display Error</Typography>
          <Typography variant="body2">{output.error}</Typography>
        </Alert>
      );
    }
    
    const columns = output.columns || [];
    const rows = output.rows || [];
    
    if (columns.length === 0) {
      return (
        <Alert severity="warning">
          <Typography variant="subtitle2">No Table Configuration</Typography>
          <Typography variant="body2">No table columns are configured for display.</Typography>
        </Alert>
      );
    }
    
    if (rows.length === 0) {
      return (
        <Alert severity="info">
          <Typography variant="subtitle2">No Data Available</Typography>
          <Typography variant="body2">No data rows are available to display.</Typography>
        </Alert>
      );
    }
    
    console.log('🎨 renderDataTable - rendering table with:', {
      columnsCount: columns.length,
      rowsCount: rows.length,
      firstRow: rows[0],
      columns: columns.map(c => ({ 
        key: c.key, 
        label: c.label, 
        type: c.type,
        dateFormat: c.dateFormat,
        currencySymbol: c.currencySymbol,
        thousandsSeparator: c.thousandsSeparator,
        decimalSeparator: c.decimalSeparator,
        decimalPlaces: c.decimalPlaces
      }))
    });
    
    // Header fields display
    const headerFields = output.headerFields || [];

    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Data Table ({output.totalRows || rows.length} items)
        </Typography>
        
        {/* Header Fields Section */}
        {headerFields.length > 0 && (
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
              📝 Summary Information
            </Typography>
            <Grid container spacing={2}>
              {headerFields.map((field, index) => {
                const getFieldWidth = (width) => {
                  switch (width) {
                    case 'half': return 6;
                    case 'third': return 4;
                    case 'quarter': return 3;
                    case 'full':
                    default: return 12;
                  }
                };
                
                return (
                  <Grid item xs={12} sm={getFieldWidth(field.width)} key={field.id || index}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                        {field.label || field.name}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 400, color: 'text.primary' }}>
                        {field.defaultValue ? resolveTemplateVariables(field.defaultValue) : (field.value || '-')}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
        
        <TableContainer component={Paper} sx={{ maxHeight: 400, overflowX: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {/* Row Number Header */}
                <TableCell 
                  align="center"
                  sx={{ 
                    width: '60px',
                    minWidth: '60px',
                    maxWidth: '60px',
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    left: 0,
                    zIndex: '10 !important',
                    borderRight: '2px solid #e0e0e0'
                  }}
                >
                  #
                </TableCell>
                {columns.map((column, index) => (
                  <TableCell 
                    key={`header-${column.key}-${column.id}-${index}`}
                    align={column.alignment || 'left'}
                    sx={{ 
                      width: getColumnWidth(column.width),
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => {
                // Create a unique row identifier
                const rowId = row.id || row._id || `row-${rowIndex}`;
                return (
                  <TableRow key={`row-${rowId}-${rowIndex}`} hover>
                    {/* Row Number Cell */}
                    <TableCell 
                      align="center"
                      sx={{ 
                        width: '60px',
                        minWidth: '60px',
                        maxWidth: '60px',
                        fontWeight: 'bold',
                        backgroundColor: '#fafafa',
                        fontSize: '0.75rem',
                        padding: '8px 12px',
                        position: 'sticky',
                        left: 0,
                        zIndex: '5 !important',
                        borderRight: '2px solid #e0e0e0'
                      }}
                    >
                      {rowIndex + 1}
                    </TableCell>
                    {columns.map((column, colIndex) => {
                      // Extract value from row using column key and dataPath
                      let value;
                      
                      // Prioritize dataPath if explicitly configured
                      if (column.dataPath) {
                        // Handle complex dataPath navigation
                        if (column.dataPath.includes('.')) {
                          // Complex path like "details.unitPrice"
                          const pathParts = column.dataPath.split('.');
                          value = row;
                          for (const part of pathParts) {
                            if (value && typeof value === 'object') {
                              value = value[part];
                            } else {
                              value = undefined;
                              break;
                            }
                          }
                        } else {
                          // Simple path - direct property access
                          value = row[column.dataPath];
                        }
                      } else {
                        // Fallback to key when no dataPath is configured
                        value = row[column.key];
                      }
                      
                      return (
                        <TableCell 
                          key={`cell-${rowId}-${column.key}-${column.id}-${rowIndex}-${colIndex}`}
                          align={column.alignment || 'left'}
                          sx={{ 
                            width: getColumnWidth(column.width),
                            fontSize: '0.75rem',
                            padding: '8px 12px'
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {formatCellValue(value, column)}
                          </Typography>
                        </TableCell>
                      );
                    })}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Detailed Report Interface
  const renderDetailedReport = (output) => {
    const sections = output.sections || [];
    
    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Detailed Report
        </Typography>
        {sections.map((section, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {section.title}
            </Typography>
            <Typography variant="body1" paragraph>
              {section.content}
            </Typography>
            {section.data && (
              <Box sx={{ mt: 2 }}>
                <pre style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', overflow: 'auto' }}>
                  {JSON.stringify(section.data, null, 2)}
                </pre>
              </Box>
            )}
          </Paper>
        ))}
      </Box>
    );
  };

  // Dashboard Interface
  const renderDashboard = (output) => {
    const widgets = output.widgets || [];
    
    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Dashboard
        </Typography>
        <Grid container spacing={2}>
          {widgets.map((widget, index) => (
                         <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
               <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {widget.title}
                  </Typography>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {widget.value}
                  </Typography>
                  {widget.description && (
                    <Typography variant="body2" color="textSecondary">
                      {widget.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Custom Template Interface
  const renderCustomTemplate = (output) => {
    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Custom Template
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body1" component="div">
            {output.template || 'No template content available'}
          </Typography>
        </Paper>
      </Box>
    );
  };

  // Default Output Renderer
  const renderDefaultOutput = (result) => {
    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Raw Output
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
          <pre style={{ margin: 0, fontSize: '0.875rem' }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </Paper>
      </Box>
    );
  };

  // Export Options Renderer
  const renderExportOptions = (exportConfig) => {
    if (!exportConfig || !exportConfig.enabled) return null;

    const formats = exportConfig.formats || ['json'];
    
    return (
      <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
        <Typography variant="h6" gutterBottom>
          Export Options
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {formats.map((format) => (
            <Button
              key={format}
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleExportData(format, exportConfig)}
            >
              Export as {format.toUpperCase()}
            </Button>
          ))}
        </Box>
      </Box>
    );
  };

  const handleExportData = async (format, exportConfig) => {
    try {
      console.log('Exporting data:', { format, exportConfig, execution });
      
      // Find the End node step result for export
      const endStepResult = stepResults.find(result => 
        result.stepType === 'end' && result.status === 'completed'
      );
      
      if (!endStepResult || !endStepResult.output) {
        alert('No data available to export');
        return;
      }
      
      const result = endStepResult.output;
      const formattedOutput = result.formattedOutput;
      
      // Prepare export data based on interface type
      let exportData = {};
      let fileName = `execution_${executionId.slice(-8)}_${new Date().toISOString().split('T')[0]}`;
      
      // Extract data based on interface type
      if (formattedOutput) {
        const interfaceType = result.interfaceType || 'summary_card';
        
        switch (interfaceType) {
          case 'data_table':
            // Export table data
            exportData = {
              title: resolveTemplateVariables(result.resultsTitle || 'Execution Results'),
              executionId: executionId,
              timestamp: new Date().toISOString(),
              headerFields: formattedOutput.headerFields || [],
              columns: formattedOutput.columns || [],
              rows: formattedOutput.rows || [],
              totalRows: formattedOutput.totalRows || formattedOutput.rows?.length || 0
            };
            break;
            
          case 'summary_card':
            // Export summary fields
            exportData = {
              title: resolveTemplateVariables(result.resultsTitle || 'Execution Results'),
              executionId: executionId,
              timestamp: new Date().toISOString(),
              fields: formattedOutput.fields || []
            };
            break;
            
          case 'detailed_report':
            // Export report sections
            exportData = {
              title: resolveTemplateVariables(result.resultsTitle || 'Execution Results'),
              executionId: executionId,
              timestamp: new Date().toISOString(),
              sections: formattedOutput.sections || []
            };
            break;
            
          case 'dashboard':
            // Export dashboard widgets
            exportData = {
              title: resolveTemplateVariables(result.resultsTitle || 'Execution Results'),
              executionId: executionId,
              timestamp: new Date().toISOString(),
              widgets: formattedOutput.widgets || []
            };
            break;
            
          default:
            // Export raw output
            exportData = {
              title: resolveTemplateVariables(result.resultsTitle || 'Execution Results'),
              executionId: executionId,
              timestamp: new Date().toISOString(),
              output: formattedOutput
            };
        }
      } else {
        // Fallback to raw result
        exportData = {
          title: 'Execution Results',
          executionId: executionId,
          timestamp: new Date().toISOString(),
          result: result
        };
      }
      
      // Export based on format
      switch (format.toLowerCase()) {
        case 'json':
          exportAsJSON(exportData, fileName);
          break;
          
        case 'csv':
          exportAsCSV(exportData, fileName);
          break;
          
        case 'excel':
        case 'xlsx':
          exportAsExcel(exportData, fileName);
          break;
          
        case 'pdf':
          alert('PDF export is not yet implemented. Please use JSON or CSV export.');
          break;
          
        default:
          exportAsJSON(exportData, fileName);
      }
      
    } catch (error) {
      console.error('Export failed:', error);
      alert(`Export failed: ${error.message}`);
    }
  };
  
  // Export as JSON
  const exportAsJSON = (data, fileName) => {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  
  // Export as CSV
  const exportAsCSV = (data, fileName) => {
    let csvContent = '';
    
    // Handle data_table interface type
    if (data.rows && data.columns) {
      // Add title and metadata
      csvContent += `"${data.title}"\n`;
      csvContent += `"Execution ID: ${data.executionId}"\n`;
      csvContent += `"Export Date: ${new Date(data.timestamp).toLocaleString()}"\n\n`;
      
      // Add header fields if present
      if (data.headerFields && data.headerFields.length > 0) {
        csvContent += '"Summary Information"\n';
        data.headerFields.forEach(field => {
          const value = field.defaultValue ? resolveTemplateVariables(field.defaultValue) : (field.value || '-');
          csvContent += `"${field.label || field.name}","${value}"\n`;
        });
        csvContent += '\n';
      }
      
      // Add table header
      csvContent += data.columns.map(col => `"${col.label}"`).join(',') + '\n';
      
      // Add table rows
      data.rows.forEach(row => {
        const rowValues = data.columns.map(col => {
          let value;
          
          // Extract value using dataPath or key
          if (col.dataPath) {
            if (col.dataPath.includes('.')) {
              const pathParts = col.dataPath.split('.');
              value = row;
              for (const part of pathParts) {
                if (value && typeof value === 'object') {
                  value = value[part];
                } else {
                  value = undefined;
                  break;
                }
              }
            } else {
              value = row[col.dataPath];
            }
          } else {
            value = row[col.key];
          }
          
          // Format the value
          const formattedValue = formatCellValue(value, col);
          
          // Escape quotes in CSV
          return `"${String(formattedValue).replace(/"/g, '""')}"`;
        });
        
        csvContent += rowValues.join(',') + '\n';
      });
    } 
    // Handle summary_card interface type
    else if (data.fields) {
      csvContent += `"${data.title}"\n`;
      csvContent += `"Execution ID: ${data.executionId}"\n`;
      csvContent += `"Export Date: ${new Date(data.timestamp).toLocaleString()}"\n\n`;
      csvContent += '"Field","Value"\n';
      
      data.fields.forEach(field => {
        csvContent += `"${field.label}","${field.formattedValue || field.value || '-'}"\n`;
      });
    }
    // Handle other interface types
    else {
      csvContent += `"${data.title}"\n`;
      csvContent += `"Execution ID: ${data.executionId}"\n`;
      csvContent += `"Export Date: ${new Date(data.timestamp).toLocaleString()}"\n\n`;
      csvContent += '"Data"\n';
      csvContent += `"${JSON.stringify(data).replace(/"/g, '""')}"\n`;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  
  // Export as Excel (using CSV format with .xlsx extension for simplicity)
  const exportAsExcel = (data, fileName) => {
    // For a proper Excel export, you would need a library like xlsx or exceljs
    // For now, we'll export as CSV with .xlsx extension as a simple solution
    // To implement proper Excel export, consider installing: npm install xlsx
    
    alert('Excel export requires additional library. Exporting as CSV instead.');
    exportAsCSV(data, fileName);
  };

  // Render steps execution details
  const renderStepsDetails = () => {
    if (!stepResults || stepResults.length === 0) {
      return (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <TimelineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Step Details Available
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Step execution details were not found for this execution.
          </Typography>
        </Paper>
      );
    }

    return stepResults.map((result, index) => {
      const hasPerformanceWarnings = result.performanceMetrics?.warnings && result.performanceMetrics.warnings.length > 0;
      const isSlowStep = result.duration && result.duration > 5000; // Steps taking more than 5 seconds
      
      return (
        <Accordion key={index} defaultExpanded={index === 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={2} width="100%">
              {getStepIcon(result.stepType, result.status)}
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {result.stepName || `Step ${index + 1}`}
                  </Typography>
                  {isSlowStep && result.status === 'completed' && (
                    <Chip 
                      label="Slow" 
                      size="small" 
                      color="warning" 
                      icon={<SpeedIcon />}
                      sx={{ height: 20 }}
                    />
                  )}
                  {hasPerformanceWarnings && (
                    <Chip 
                      label={`${result.performanceMetrics.warnings.length} Warning${result.performanceMetrics.warnings.length > 1 ? 's' : ''}`}
                      size="small" 
                      color="error" 
                      icon={<WarningIcon />}
                      sx={{ height: 20 }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {result.stepType} • {formatDuration(result.duration)}
                </Typography>
              </Box>
              <Chip
                label={result.status}
                color={getStatusColor(result.status)}
                size="small"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {/* Performance Warnings - Show at top if present */}
              {hasPerformanceWarnings && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Performance Issues Detected:
                    </Typography>
                    {result.performanceMetrics.warnings.map((warning, idx) => (
                      <Typography key={idx} variant="body2" display="block">
                        • {warning.message}
                        {warning.threshold && warning.actual && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (Threshold: {warning.threshold}ms, Actual: {warning.actual}ms)
                          </Typography>
                        )}
                      </Typography>
                    ))}
                  </Alert>
                </Grid>
              )}
              
              {/* Step Details */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Step Details
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="textSecondary">Status:</Typography>
                        <Chip label={result.status} color={getStatusColor(result.status)} size="small" />
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="textSecondary">Duration:</Typography>
                        <Typography variant="body2">{formatDuration(result.duration)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="textSecondary">Type:</Typography>
                        <Typography variant="body2">{result.stepType}</Typography>
                      </Box>
                      {result.startedAt && (
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="textSecondary">Started:</Typography>
                          <Typography variant="body2">
                            {new Date(result.startedAt).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Step Output */}
              <Grid size={{ xs: 12, md: 8 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2">
                        Output
                      </Typography>
                      {result.output && (
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewStepResult(result)}
                        >
                          View Details
                        </Button>
                      )}
                    </Box>
                    
                    {result.error ? (
                      <Box>
                        <Alert severity="error">
                          <Typography variant="subtitle2">Error:</Typography>
                          <Typography variant="body2">{result.error}</Typography>
                        </Alert>
                        
                        {/* Show diagnostics for file processing errors */}
                        {result.output?.results?.some(r => r.diagnostics) && (
                          <Paper variant="outlined" sx={{ p: 2, mt: 2, backgroundColor: '#fff3e0', borderColor: '#ff9800' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#e65100', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <WarningIcon fontSize="small" />
                              Diagnostic Information
                            </Typography>
                            {result.output.results.filter(r => r.diagnostics).map((fileResult, idx) => (
                              <Box key={idx} sx={{ mb: idx < result.output.results.filter(r => r.diagnostics).length - 1 ? 2 : 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  File: {fileResult.fileName}
                                </Typography>
                                {fileResult.diagnostics && (
                                  <Box sx={{ pl: 2 }}>
                                    <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', backgroundColor: '#fff', p: 1, borderRadius: 1, mb: 1 }}>
                                      <strong>Attempted Path:</strong> {fileResult.diagnostics.attemptedPath}
                                    </Typography>
                                    <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                                      <strong>Timestamp:</strong> {new Date(fileResult.diagnostics.timestamp).toLocaleString()}
                                    </Typography>
                                    
                                    {fileResult.diagnostics.fileInfo && (
                                      <Box sx={{ mb: 1 }}>
                                        <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
                                          File Info:
                                        </Typography>
                                        <Box sx={{ pl: 2 }}>
                                          <Typography variant="caption" component="div">
                                            • Uploaded to S3: {fileResult.diagnostics.fileInfo.uploadedToS3 ? 'Yes' : 'No'}
                                          </Typography>
                                          {fileResult.diagnostics.fileInfo.s3Key && (
                                            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                                              • S3 Key: {fileResult.diagnostics.fileInfo.s3Key}
                                            </Typography>
                                          )}
                                          {fileResult.diagnostics.fileInfo.fileSize && (
                                            <Typography variant="caption" component="div">
                                              • Size: {formatFileSize(fileResult.diagnostics.fileInfo.fileSize)}
                                            </Typography>
                                          )}
                                          {fileResult.diagnostics.fileInfo.uploadedAt && (
                                            <Typography variant="caption" component="div">
                                              • Uploaded: {new Date(fileResult.diagnostics.fileInfo.uploadedAt).toLocaleString()}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    )}
                                    
                                    {fileResult.diagnostics.uploadDirExists !== undefined && (
                                      <Box sx={{ mb: 1 }}>
                                        <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
                                          Upload Directory Status:
                                        </Typography>
                                        <Box sx={{ pl: 2 }}>
                                          <Typography variant="caption" component="div">
                                            • Exists: {fileResult.diagnostics.uploadDirExists ? 'Yes' : 'No'}
                                          </Typography>
                                          {fileResult.diagnostics.uploadDirExists && (
                                            <Typography variant="caption" component="div">
                                              • Files in directory: {fileResult.diagnostics.filesInUploadDir}
                                            </Typography>
                                          )}
                                          {fileResult.diagnostics.matchingFiles && fileResult.diagnostics.matchingFiles.length > 0 && (
                                            <Typography variant="caption" component="div">
                                              • Matching files found: {fileResult.diagnostics.matchingFiles.join(', ')}
                                            </Typography>
                                          )}
                                          {fileResult.diagnostics.uploadDirError && (
                                            <Typography variant="caption" component="div" color="error">
                                              • Error: {fileResult.diagnostics.uploadDirError}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    )}
                                    
                                    {fileResult.diagnostics.parentDirExists !== undefined && (
                                      <Box sx={{ mb: 1 }}>
                                        <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
                                          Parent Directory:
                                        </Typography>
                                        <Box sx={{ pl: 2 }}>
                                          <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                            {fileResult.diagnostics.parentDirPath}
                                          </Typography>
                                          <Typography variant="caption" component="div">
                                            • Exists: {fileResult.diagnostics.parentDirExists ? 'Yes' : 'No'}
                                          </Typography>
                                          {fileResult.diagnostics.parentDirExists && (
                                            <Typography variant="caption" component="div">
                                              • Files in directory: {fileResult.diagnostics.filesInParentDir}
                                            </Typography>
                                          )}
                                          {fileResult.diagnostics.parentDirError && (
                                            <Typography variant="caption" component="div" color="error">
                                              • Error: {fileResult.diagnostics.parentDirError}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    )}
                                    
                                    <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
                                      <Typography variant="caption" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        💡 Possible Causes:
                                      </Typography>
                                      <Typography variant="caption" component="div">
                                        • File was uploaded but deleted before processing (check timing)
                                      </Typography>
                                      <Typography variant="caption" component="div">
                                        • File upload may have failed silently
                                      </Typography>
                                      <Typography variant="caption" component="div">
                                        • Server restart or process manager may have cleared temp files
                                      </Typography>
                                      <Typography variant="caption" component="div">
                                        • Consider enabling S3 upload in collect_input step configuration
                                      </Typography>
                                    </Alert>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Paper>
                        )}
                      </Box>
                    ) : result.output ? (
                      <Box>
                        <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                          <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                            {JSON.stringify(result.output, null, 2)}
                          </pre>
                        </Paper>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                        No output data available
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Performance Breakdown - Show if available */}
              {result.performanceMetrics && (
                <Grid size={{ xs: 12 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                        <SpeedIcon color="primary" /> Performance Breakdown
                      </Typography>
                      
                      {/* Time Breakdown */}
                      {result.performanceMetrics.breakdown && result.performanceMetrics.breakdown.length > 0 && (
                        <Box mb={3}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Time Breakdown:
                          </Typography>
                          <List dense>
                            {result.performanceMetrics.breakdown.map((item, idx) => (
                              <ListItem key={idx} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <Typography variant="body2">{item.operation}</Typography>
                                      <Box display="flex" alignItems="center" gap={2}>
                                        <Chip 
                                          label={`${item.percentage}%`} 
                                          size="small" 
                                          color={item.percentage > 50 ? 'error' : item.percentage > 25 ? 'warning' : 'default'} 
                                          variant="outlined"
                                        />
                                        <Typography variant="body2" fontWeight="bold">
                                          {formatDuration(item.duration)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  }
                                  secondary={item.details}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      
                      {/* Detailed Metrics */}
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Detailed Metrics:
                      </Typography>
                      <Grid container spacing={2}>
                        {/* Pre-execution Operations */}
                        {(result.performanceMetrics.cacheCleanupTime > 0 || 
                          result.performanceMetrics.authenticationCheckTime > 0 || 
                          result.performanceMetrics.validationTime > 0) && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="Pre-execution Operations" size="small" color="info" />
                            </Grid>
                            {result.performanceMetrics.cacheCleanupTime > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Cache Cleanup</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.cacheCleanupTime)}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.authenticationCheckTime > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Authentication Check</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.authenticationCheckTime)}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.validationTime > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Validation Time</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.validationTime)}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {/* File Operations */}
                        {(result.performanceMetrics.fileUploadTime > 0 || 
                          result.performanceMetrics.fileProcessingTime > 0) && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="File Operations" size="small" color="secondary" />
                            </Grid>
                            {result.performanceMetrics.fileUploadTime > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">File Upload</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.fileUploadTime)}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.fileProcessingTime > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">File Processing</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.fileProcessingTime)}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.fileCount > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Files Processed</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.fileCount} files
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.totalFileSize > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Total Size</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatFileSize(result.performanceMetrics.totalFileSize)}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {/* AI Processing */}
                        {result.performanceMetrics.aiProcessingTime > 0 && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="AI Processing" size="small" color="success" />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Typography variant="caption" color="text.secondary">AI Processing Time</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(result.performanceMetrics.aiProcessingTime)}
                              </Typography>
                            </Grid>
                            {result.performanceMetrics.aiModelUsed && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Model</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.aiModelUsed}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.aiTokens > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Tokens Used</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.aiTokens.toLocaleString()}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {/* Network & API */}
                        {(result.performanceMetrics.networkLatency > 0 || 
                          result.performanceMetrics.apiCallTime > 0) && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="Network & API" size="small" color="warning" />
                            </Grid>
                            {result.performanceMetrics.networkLatency > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Network Latency</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.networkLatency)}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.apiCallTime > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">API Call Time</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatDuration(result.performanceMetrics.apiCallTime)}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.apiCallCount > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">API Calls</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.apiCallCount}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {/* Learning Corrections */}
                        {result.performanceMetrics.learningCorrectionsTime > 0 && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="Learning Corrections" size="small" />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Typography variant="caption" color="text.secondary">Learning Time</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDuration(result.performanceMetrics.learningCorrectionsTime)}
                              </Typography>
                            </Grid>
                            {result.performanceMetrics.learningCorrectionsCount > 0 && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Corrections Captured</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.learningCorrectionsCount}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {/* OCR & Vision Processing (Document OCR & Vision steps) */}
                        {(result.performanceMetrics.ocrEngine || result.performanceMetrics.visionModel) && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="OCR & Vision Processing" size="small" color="info" />
                            </Grid>
                            {result.performanceMetrics.ocrEngine && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">OCR Engine</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.ocrEngine}
                                  {result.performanceMetrics.awsTextract && ' (AWS Textract)'}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.visionModel && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Vision Model</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.visionModel}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.visionMethod && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Vision Method</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.visionMethod}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.processingMode && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Processing Mode</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.processingMode}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {/* LLM Processing (AI Field Mapping steps) */}
                        {result.performanceMetrics.llmModel && (
                          <>
                            <Grid size={{ xs: 12 }}>
                              <Chip label="LLM Processing" size="small" color="secondary" />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Typography variant="caption" color="text.secondary">LLM Model</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {result.performanceMetrics.llmModel}
                              </Typography>
                            </Grid>
                            {result.performanceMetrics.maxTokens && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Max Tokens</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.maxTokens.toLocaleString()}
                                </Typography>
                              </Grid>
                            )}
                            {result.performanceMetrics.includeConfidenceScores !== undefined && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">Confidence Scores</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {result.performanceMetrics.includeConfidenceScores ? 'Enabled' : 'Disabled'}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  // Render files tab
  const renderFilesTab = () => {
    return (
      <Box>
        
        {/* Input Files Section */}
        {execution?.inputData?.files && execution.inputData.files.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Input Files
            </Typography>
            <List sx={{ p: 0 }}>
              {execution.inputData.files.map((file, index) => (
                <Card key={index} sx={{ mb: 1 }}>
                  <ListItem
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      }
                    }}
                    onClick={() => handleFileClick(file)}
                  >
                    <ListItemIcon>
                      {isPreviewableFile(file.originalName) ? (
                        <Box sx={{ position: 'relative' }}>
                          <AssignmentIcon color="primary" />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -2,
                              right: -2,
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: 'success.main',
                              border: '1px solid white'
                            }}
                          />
                        </Box>
                      ) : (
                        <AssignmentIcon color="primary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight="medium">
                          {file.originalName}
                        </Typography>
                      }
                    />
                    <Box sx={{ ml: 2, flex: 1 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        {file.fileType} • {formatFileSize(file.fileSize || 0)}
                        {isPreviewableFile(file.originalName) ? ' • Click to preview' : ''}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        Uploaded: {new Date(file.uploadedAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {file.filename || file.fileId || 'No ID'} | Type: {file.fileType || 'Unknown'}
                      </Typography>
                    </Box>
                  </ListItem>
                </Card>
              ))}
            </List>
          </Box>
        )}

        {/* Output Files Section */}
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Output Files
        </Typography>
        {outputFiles.length > 0 ? (
          <List sx={{ p: 0 }}>
            {outputFiles.map((file, index) => (
              <Card key={index} sx={{ mb: 1 }}>
                <ListItem
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                  onClick={() => handleFileClick(file)}
                >
                  <ListItemIcon>
                    {isPreviewableFile(file.fileName) ? (
                      <Box sx={{ position: 'relative' }}>
                        <AssignmentIcon color="primary" />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -2,
                            right: -2,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'success.main',
                            border: '1px solid white'
                          }}
                        />
                      </Box>
                    ) : (
                      <AssignmentIcon color="primary" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" fontWeight="medium">
                        {file.fileName}
                      </Typography>
                    }
                  />
                  <Box sx={{ ml: 2, flex: 1 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                      {file.fileType} • {formatFileSize(file.fileSize || 0)}
                      {isPreviewableFile(file.fileName) ? ' • Click to preview' : ''}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                      {new Date(file.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      ID: {file.fileId || 'No ID'} | Type: {file.fileType || 'Unknown'}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadFile(file.fileId, file.fileName);
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </ListItem>
              </Card>
            ))}
          </List>
        ) : (
          <Alert severity="info">
            No output files were generated during this execution.
          </Alert>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box 
        sx={{
          width: '100%',
          overflowX: 'hidden'
        }}
      >
        <Box 
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            mt: isMobile ? 2 : 4,
            px: isMobile ? 1 : 2
          }}
        >
          <BreadcrumbsNav />
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={60} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (error || !execution) {
    return (
      <Box 
        sx={{
          width: '100%',
          overflowX: 'hidden'
        }}
      >
        <Box 
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            mt: isMobile ? 2 : 4,
            px: isMobile ? 1 : 2
          }}
        >
          <BreadcrumbsNav />
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Execution not found'}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              // Navigate to project page with Executions tab active (tab index 1)
              navigate(`/company/${companyId}/project/${projectId}?tab=1`);
            }}
            fullWidth={isMobile}
          >
            Back to Project
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        width: '100%',
        overflowX: 'hidden'
      }}
    >
      <Box 
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          mt: isMobile ? 2 : 4,
          px: isMobile ? 1 : 2
        }}
      >
        <BreadcrumbsNav />
      
      {/* Header */}
      <Box 
        display="flex" 
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between" 
        alignItems={isMobile ? "stretch" : "center"}
        gap={isMobile ? 2 : 0}
        mb={isMobile ? 2 : 4}
        sx={{ minWidth: 0 }} // Prevent overflow
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" gutterBottom>
            {execution.status === 'completed' ? 'Execution Results' : 'Execution Details'}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant={isMobile ? "caption" : "body1"} color="textSecondary">
              Execution #{executionId.slice(-8)} • {execution.projectName || 'Project'}
            </Typography>
            {autoRefresh && (
              <Chip 
                label="Auto-refreshing" 
                color="info" 
                size="small" 
                icon={<RefreshIcon />}
              />
            )}
          </Box>
          {!isMobile && (
            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', mt: 0.5 }}>
              ID: {executionId}
            </Typography>
          )}
        </Box>
        <Box 
          display="flex" 
          flexDirection={isMobile ? "column" : "row"} 
          gap={isMobile ? 1 : 2}
          sx={{ 
            width: isMobile ? "100%" : "auto",
            minWidth: 0, // Prevent overflow
            flexShrink: 0
          }}
        >
          <Button
            variant="outlined"
            startIcon={isMobile ? null : <ArrowBackIcon />}
            onClick={() => {
              // Navigate to project page with Executions tab active (tab index 1)
              navigate(`/company/${companyId}/project/${projectId}?tab=1`);
            }}
            fullWidth={isMobile}
            size="small"
            sx={{
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              py: isMobile ? 0.5 : 0.75
            }}
          >
            {isMobile ? <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} /> : null}
            Back to Project
          </Button>
          <Button
            variant="outlined"
            startIcon={isMobile ? null : <RefreshIcon />}
            onClick={fetchExecutionDetails}
            fullWidth={isMobile}
            size="small"
            sx={{
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              py: isMobile ? 0.5 : 0.75
            }}
          >
            {isMobile ? <RefreshIcon fontSize="small" sx={{ mr: 0.5 }} /> : null}
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Execution Summary */}
      <Paper elevation={1} sx={{ p: isMobile ? 2 : 3, mb: isMobile ? 2 : 4 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box textAlign="center">
              <Avatar sx={{ bgcolor: getStatusColor(execution.status) + '.main', mx: 'auto', mb: 1 }}>
                {execution.status === 'completed' ? <CheckCircleIcon /> : <ErrorIcon />}
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {execution.status === 'completed' ? 'Completed' : execution.status}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Status
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold" color="primary">
                {stepResults?.length || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Processing Steps
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {formatDuration(
                  // For in-progress executions, prefer actualProcessingTime (completed steps only)
                  // For completed/failed executions, use actualProcessingTime if available, otherwise fall back to duration
                  (execution.status === 'in_progress' || execution.status === 'waiting_input') 
                    ? (execution.actualProcessingTime || 0)
                    : (execution.actualProcessingTime || execution.duration)
                )}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Duration
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box textAlign="center">
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {execution.filesCount || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Files Processed
              </Typography>
            </Box>
          </Grid>
          {/* Token Usage Display */}
          {execution?.metrics?.billableObjectsCumulative && (
            <Grid size={{ xs: 12, md: 2 }}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  🪙 {execution.metrics.billableObjectsCumulative}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Tokens Used
                </Typography>
              </Box>
            </Grid>
          )}
          {/* Billable Events Display */}
          {execution?.metrics?.billableEvents && (
            <Grid size={{ xs: 12, md: 2 }}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight="bold" color="secondary.main">
                  📊 {execution.metrics.billableEvents}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Billable Events
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Execution ID Section */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
            🔍 Execution Information
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="textSecondary">Execution ID:</Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 500 }}>
                {executionId}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="textSecondary">Project:</Typography>
              <Typography variant="body1">
                {execution.projectName || 'Unknown Project'}
              </Typography>
            </Grid>
            {execution?.pwaVersion && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="textSecondary">PWA Version:</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 500 }}>
                  v{execution.pwaVersion}
                  {execution.pwaBuildDate && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (Build: {execution.pwaBuildDate})
                    </Typography>
                  )}
                </Typography>
              </Grid>
            )}
            {execution?.variables?.userFullName && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="textSecondary">User:</Typography>
                <Typography variant="body1">
                  {execution.variables.userFullName}
                </Typography>
              </Grid>
            )}
            {execution?.variables?.userAdditionalInfo && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="textSecondary">Additional Info:</Typography>
                <Typography variant="body1">
                  {execution.variables.userAdditionalInfo}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="textSecondary">Started At:</Typography>
            <Typography variant="body1">
              {new Date(execution.startedAt).toLocaleString()}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="textSecondary">Completed At:</Typography>
            <Typography variant="body1">
              {execution.completedAt ? new Date(execution.completedAt).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs for Results, Steps, and Files */}
      <Paper elevation={1}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab 
            icon={execution?.status === 'completed' ? <CheckCircleIcon /> : <InfoIcon />} 
            label={execution?.status === 'completed' ? 'Results' : 'Status'} 
          />
          <Tab icon={<TimelineIcon />} label="Execution Steps" />
          <Tab icon={<AssignmentIcon />} label="Files" />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {/* Results Tab */}
          {activeTab === 0 && (
            <Box>
              {renderExecutionResults()}
            </Box>
          )}

          {/* Steps Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h5" fontWeight="bold" mb={3}>
                Step-by-Step Execution Details
              </Typography>
              {renderStepsDetails()}
            </Box>
          )}

          {/* Files Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h5" fontWeight="bold" mb={3}>
                Files
              </Typography>
              {renderFilesTab()}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Result Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Step Result Details
        </DialogTitle>
        <DialogContent>
          {selectedResult && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedResult.stepName}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(selectedResult.output, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog
        open={filePreviewDialog}
        onClose={() => {
          // Clean up blob URL to prevent memory leaks
          if (selectedFile?.blob && selectedFile?.previewUrl) {
            URL.revokeObjectURL(selectedFile.previewUrl);
          }
          setFilePreviewDialog(false);
          setSelectedFile(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <AssignmentIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {selectedFile?.originalName || selectedFile?.fileName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedFile && `${formatFileSize(selectedFile.fileSize || 0)} • ${selectedFile.fileType}`}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedFile && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              {selectedFile.isImage ? (
                <Box>
                  {selectedFile.error ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Preview Error</Typography>
                      <Typography variant="body2">{selectedFile.error}</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleFileClick(selectedFile)}
                        sx={{ mt: 1 }}
                      >
                        Retry
                      </Button>
                    </Alert>
                  ) : selectedFile.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        Loading image preview...
                      </Typography>
                    </Box>
                  ) : selectedFile.previewUrl ? (
                    <img
                      src={selectedFile.previewUrl}
                      alt={selectedFile.originalName || selectedFile.fileName}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '70vh',
                        objectFit: 'contain',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                </Box>
              ) : selectedFile.isPdf ? (
                <Box>
                  {selectedFile.error ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Preview Error</Typography>
                      <Typography variant="body2">{selectedFile.error}</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleFileClick(selectedFile)}
                        sx={{ mt: 1 }}
                      >
                        Retry
                      </Button>
                    </Alert>
                  ) : selectedFile.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        Loading PDF preview...
                      </Typography>
                    </Box>
                  ) : selectedFile.previewUrl ? (
                    <iframe
                      src={selectedFile.previewUrl}
                      title={selectedFile.originalName || selectedFile.fileName}
                      style={{
                        width: '100%',
                        height: '70vh',
                        border: 'none',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <Alert severity="error" sx={{ mt: 2, display: 'none' }}>
                    Unable to load PDF preview. The file may not be accessible or may have been deleted.
                  </Alert>
                </Box>
              ) : (
                <Alert severity="info">
                  <Typography variant="subtitle2">File Preview Not Available</Typography>
                  <Typography variant="body2">
                    Preview is only available for image files (PNG, JPG, GIF, etc.) and PDF files. 
                    This file type ({selectedFile.fileType}) cannot be previewed in the browser.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilePreviewDialog(false)}>
            Close
          </Button>
          {selectedFile && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => {
                // For input files, use filename; for output files, use fileId
                const fileId = selectedFile.filename || selectedFile.fileId;
                const fileName = selectedFile.fileName || selectedFile.originalName;
                
                handleDownloadFile(fileId, fileName);
              }}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default ProjectExecutionResults; 